import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  Lock,
  Unlock,
  Calendar,
  Pencil,
  Check,
  X,
  Bot,
  UserCheck,
  CalendarClock,
  Plus,
  Clock,
  Wallet,
  AlertTriangle,
  ChevronDown,
  GripVertical,
} from "lucide-react";
import { LiquidityWithFees } from "./LiquidityWithFees";
import { format } from "date-fns";
import { useRecoilValue } from "recoil";
import { userProfileData } from "../RecoilAtom";
import { useIsMobile } from "./hooks/useMobile";
import { getBackgroundColor } from "../../utils/helpers";
import { useTheme } from "../../contexts/ThemeProvider";
import DateTimePicker from "../common/DateTimePicker";

const STEP_TO_SECTION = {
  question: "section-question",
  description: "section-description",
  outcomes: "section-outcomes",
  visibility: "section-visibility",
  resolver: "section-resolver",
  resolution: "section-resolution",
  liquidity: "section-liquidity",
};

const ActiveSectionGlow = ({ active, children, className }) => (
  <div className="gm-mdp-section-glow">{children}</div>
);

const SwipableOutcomeBar = ({
  outcome,
  color,
  onProbabilityChange,
  onEdit,
  onRemove,
  onToggleLock,
  canRemove,
  isDuplicate,
  disabled,
}) => {
  const barRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback(
    (clientX) => {
      if (!barRef.current || outcome.locked || outcome.isUnclassified || disabled) return;
      const rect = barRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.round(
        Math.max(1, Math.min(99, (x / rect.width) * 100)),
      );
      onProbabilityChange(outcome.id, percentage);
    },
    [onProbabilityChange, outcome.id, outcome.locked, outcome.isUnclassified, disabled],
  );

  const handleStart = (clientX) => {
    if (outcome.locked || outcome.isUnclassified || disabled) return;
    setIsDragging(true);
    handleDrag(clientX);
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => handleDrag(e.clientX);
    const handleTouchMove = (e) => {
      e.preventDefault();
      handleDrag(e.touches[0].clientX);
    };
    const handleEnd = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, handleDrag]);

  const barClass = [
    "gm-mdp-outcome-bar",
    outcome.isUnclassified ? "gm-mdp-outcome-bar--unclassified" : "",
    outcome.locked
      ? "gm-mdp-outcome-bar--locked"
      : isDragging
        ? "gm-mdp-outcome-bar--dragging"
        : "gm-mdp-outcome-bar--swipable",
    isDuplicate && !outcome.isUnclassified
      ? "gm-mdp-outcome-bar--duplicate"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={barRef}
      className={barClass}
      style={{ backgroundColor: color }}
      onMouseDown={(e) => {
        if (e.target.closest("input, button")) return;
        handleStart(e.clientX);
      }}
      onTouchStart={(e) => {
        if (e.target.closest("input, button")) return;
        handleStart(e.touches[0].clientX);
      }}
    >
      {!outcome.locked && !outcome.isUnclassified && (
        <motion.div
          className="gm-mdp-drag-thumb"
          animate={{ left: `calc(${outcome.probability}% - 2px)` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
      <div className="gm-mdp-outcome-label-wrap">
        <span className="gm-mdp-outcome-label-text">{outcome.label}</span>
        {outcome.isUnclassified && (
          <div className="tooltipicon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="icon"
            >
              <g clip-path="url(#clip0_1550_843)">
                <path
                  d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z"
                  stroke="#797979"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 12V9"
                  stroke="#797979"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 6H9.0075"
                  stroke="#797979"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1550_843">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div className="tooltipmain">
              Covers cases where no single option or multiple options are
              correct, or where the event cannot be resolved as expected.
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="10"
                viewBox="0 0 13 10"
                fill="none"
                className="tooltiparrow"
              >
                <path
                  d="M7.30183 8.93333C6.90183 9.46667 6.10183 9.46667 5.70183 8.93333L0.201832 1.6C-0.292596 0.940763 0.177786 -2.54862e-07 1.00183 -1.82822e-07L12.0018 7.78829e-07C12.8259 8.50869e-07 13.2963 0.940764 12.8018 1.6L7.30183 8.93333Z"
                  fill="#2A2A2A"
                />
              </svg>
            </div>
          </div>
        )}
        {isDuplicate && !outcome.isUnclassified && (
          <span className="gm-mdp-duplicate-tag">Duplicate</span>
        )}
      </div>
      <div className="gm-mdp-outcome-actions">
        {!outcome.isUnclassified && !disabled && (
          <div className="gm-mdp-outcome-hover-actions">
            {onToggleLock && (
              <button
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLock();
                }}
                className="gm-mdp-outcome-action-btn"
                title={
                  outcome.locked ? "Unlock probability" : "Lock probability"
                }
              >
                {outcome.locked ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M15.8333 9.16663H4.16667C3.24619 9.16663 2.5 9.91282 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91282 16.7538 9.16663 15.8333 9.16663Z"
                      stroke="#212121"
                      stroke-width="1.57"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5.83337 5.83329C5.83337 4.72822 6.27236 3.66842 7.05376 2.88701C7.83516 2.10561 8.89497 1.66663 10 1.66663C11.1051 1.66663 12.1649 2.10561 12.9463 2.88701C13.7277 3.66842 14.1667 4.72822 14.1667 5.83329V9.16663"
                      stroke="#212121"
                      stroke-width="1.57"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M15.8333 9.16666H4.16667C3.24619 9.16666 2.5 9.91285 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91285 16.7538 9.16666 15.8333 9.16666Z"
                      stroke="#212121"
                      stroke-width="1.57"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5.83337 9.16666V5.83332C5.83337 4.72825 6.27236 3.66845 7.05376 2.88704C7.83516 2.10564 8.89497 1.66666 10 1.66666C11.1051 1.66666 12.1649 2.10564 12.9463 2.88704C13.7277 3.66845 14.1667 4.72825 14.1667 5.83332V9.16666"
                      stroke="#212121"
                      stroke-width="1.57"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
            {!outcome.locked && onEdit && (
              <button
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="gm-mdp-outcome-action-btn"
                title="Edit label"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333V10"
                    stroke="#212121"
                    stroke-width="1.57143"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.3065 2.18769C15.638 1.85617 16.0877 1.66992 16.5565 1.66992C17.0254 1.66992 17.475 1.85617 17.8065 2.18769C18.138 2.51921 18.3243 2.96885 18.3243 3.43769C18.3243 3.90653 18.138 4.35617 17.8065 4.68769L10.2957 12.1994C10.0978 12.3971 9.85335 12.5418 9.58485 12.6202L7.19068 13.3202C7.11897 13.3411 7.04296 13.3424 6.97061 13.3238C6.89825 13.3053 6.8322 13.2676 6.77939 13.2148C6.72657 13.162 6.68892 13.096 6.67038 13.0236C6.65185 12.9512 6.6531 12.8752 6.67402 12.8035L7.37402 10.4094C7.45279 10.1411 7.59779 9.8969 7.79568 9.69936L15.3065 2.18769Z"
                    stroke="#212121"
                    stroke-width="1.57143"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        <motion.span
          className="gm-mdp-outcome-pct"
          animate={isDragging ? { scale: 1.1 } : { scale: 1 }}
        >
          {outcome.probability}%
        </motion.span>
      </div>
    </div>
  );
};

export const MarketDetailPreview = ({
  marketData,
  onConfirm,
  isProcessing,
  onEdit,
  activeStep,
  onNavigateToStep,
  forceManual,
  walletError,
  wallet,
  onConnectWallet,
  gasFeeInUsdt,
  oracleFeeInUsdt,
  isLoadingFees,
  onLiquidityAmountChange,
}) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const userProfile = useRecoilValue(userProfileData);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [marketInfoOpen, setMarketInfoOpen] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showLiquidityEditor, setShowLiquidityEditor] = useState(false);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  const dismissOnboarding = useCallback(() => {
    if (showOnboarding) setShowOnboarding(false);
  }, [showOnboarding]);

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const confirmEdit = () => {
    if (onEdit && editingField) {
      if (editingField === "liquidity") {
        const parsed = parseInt(editValue, 10);
        if (!isNaN(parsed) && parsed >= 10) onEdit("liquidity", parsed);
      } else if (
        editingField === "question" ||
        editingField === "description"
      ) {
        onEdit(editingField, editValue);
      }
    }
    setEditingField(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  const nonUnclassifiedOutcomes = marketData.outcomes.filter(
    (o) => !o.isUnclassified,
  );

  const getOutcomeColor = (outcomeId, index) => {
    const nonUnclassifiedIndex = nonUnclassifiedOutcomes.findIndex(
      (o) => o.id === outcomeId,
    );
    const colorIndex =
      nonUnclassifiedIndex !== -1 ? nonUnclassifiedIndex : index;
    return getBackgroundColor(colorIndex, theme, marketData.outcomes.length);
  };

  const normalizeOutcomeLabel = (label) => label.trim().toLowerCase();

  const getDuplicateOutcomeLabels = (outcomes = marketData.outcomes) => {
    const seen = new Set();
    const duplicates = new Set();
    outcomes.forEach((outcome) => {
      if (outcome.isUnclassified) return;
      const normalized = normalizeOutcomeLabel(outcome.label);
      if (!normalized) return;
      if (seen.has(normalized)) {
        duplicates.add(normalized);
        return;
      }
      seen.add(normalized);
    });
    return duplicates;
  };

  const isDuplicateOutcomeLabel = (label, currentIndex) => {
    const normalized = normalizeOutcomeLabel(label);
    if (!normalized) return false;
    return marketData.outcomes.some(
      (outcome, index) =>
        index !== currentIndex &&
        !outcome.isUnclassified &&
        normalizeOutcomeLabel(outcome.label) === normalized,
    );
  };

  const saveOutcomeLabel = (index, label) => {
    if (isProcessing) return;
    const trimmedLabel = label.trim();
    if (!trimmedLabel || isDuplicateOutcomeLabel(trimmedLabel, index)) return;
    const newOutcomes = [...marketData.outcomes];
    newOutcomes[index] = { ...newOutcomes[index], label: trimmedLabel };
    if (onEdit) onEdit("outcomes", newOutcomes);
    setEditingField(null);
  };

  const addOutcome = () => {
    if (isProcessing) return;
    const hasEmptyOutcome = marketData.outcomes.some(
      (o) => !o.isUnclassified && !o.label.trim(),
    );
    if (hasEmptyOutcome) return;

    const fixedTotal = marketData.outcomes
      .filter((outcome) => outcome.isUnclassified || outcome.locked)
      .reduce((sum, outcome) => sum + outcome.probability, 0);
    const editableOutcomes = marketData.outcomes.filter(
      (outcome) => !outcome.isUnclassified && !outcome.locked,
    );
    const availableProbability = Math.max(0, 100 - fixedTotal);
    const slotCount = editableOutcomes.length + 1;
    const baseProbability =
      slotCount > 0 ? Math.floor(availableProbability / slotCount) : 0;
    const remainder =
      slotCount > 0 ? availableProbability - baseProbability * slotCount : 0;

    const redistributedById = new Map(
      editableOutcomes.map((outcome, index) => [
        outcome.id,
        baseProbability + (index < remainder ? 1 : 0),
      ]),
    );

    const newOutcomeProbability =
      baseProbability + (editableOutcomes.length < remainder ? 1 : 0);
    const newOutcome = {
      id: crypto.randomUUID(),
      label: "",
      probability: newOutcomeProbability,
    };

    const redistributedOutcomes = marketData.outcomes.map((outcome) => {
      if (!redistributedById.has(outcome.id)) return outcome;
      return { ...outcome, probability: redistributedById.get(outcome.id) };
    });

    const unclassifiedIdx = redistributedOutcomes.findIndex(
      (outcome) => outcome.isUnclassified,
    );
    const nextOutcomes = [...redistributedOutcomes];
    if (unclassifiedIdx >= 0) {
      nextOutcomes.splice(unclassifiedIdx, 0, newOutcome);
    } else {
      nextOutcomes.push(newOutcome);
    }
    if (onEdit) onEdit("outcomes", nextOutcomes);
    const editIdx =
      unclassifiedIdx >= 0 ? unclassifiedIdx : nextOutcomes.length - 1;
    setTimeout(() => startEditing(`outcome-${editIdx}`, ""), 50);
  };

  const handleOutcomeProbabilityChange = (id, value) => {
    if (isProcessing) return;
    const outcomes = [...marketData.outcomes];
    const target = outcomes.find((o) => o.id === id);
    if (!target || target.locked || target.isUnclassified) return;

    const lockedTotal = outcomes.reduce(
      (sum, o) =>
        (o.locked || o.isUnclassified) && o.id !== id
          ? sum + o.probability
          : sum,
      0,
    );
    const unlockedOthers = outcomes.filter(
      (o) => o.id !== id && !o.locked && !o.isUnclassified,
    );
    const maxAllowed = 100 - lockedTotal - unlockedOthers.length;
    const newValue = Math.min(maxAllowed, Math.max(1, value));

    let remainingPercentage = 100 - lockedTotal - newValue;

    const updated = outcomes.map((o) => {
      if (o.id === id) return { ...o, probability: newValue };
      return o;
    });

    if (unlockedOthers.length > 0) {
      const minRemaining = unlockedOthers.length;
      const distributable = Math.max(0, remainingPercentage - minRemaining);
      const equalShare = Math.floor(distributable / unlockedOthers.length);
      const lastShare =
        remainingPercentage - equalShare * (unlockedOthers.length - 1);

      const unlockedIds = unlockedOthers.map((o) => o.id);
      const result = updated.map((o) => {
        const idx = unlockedIds.indexOf(o.id);
        if (idx < 0) return o;
        return {
          ...o,
          probability:
            idx === unlockedIds.length - 1
              ? Math.max(1, lastShare)
              : Math.max(1, equalShare),
        };
      });
      const total = result.reduce((s, o) => s + o.probability, 0);
      const diff = 100 - total;
      if (diff !== 0) {
        const lastUnlockedId = unlockedIds[unlockedIds.length - 1];
        const fixed = result.map((o) =>
          o.id === lastUnlockedId
            ? { ...o, probability: o.probability + diff }
            : o,
        );
        if (onEdit) onEdit("outcomes", fixed);
        return;
      }
      if (onEdit) onEdit("outcomes", result);
      return;
    }

    if (onEdit) onEdit("outcomes", updated);
  };

  const resolutionDateObj = marketData.resolutionDate
    ? marketData.resolutionDate instanceof Date
      ? marketData.resolutionDate
      : new Date(marketData.resolutionDate)
    : null;
  const daysRemaining = resolutionDateObj
    ? Math.max(
        0,
        Math.ceil(
          (resolutionDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  const isActive = (section) => {
    if (!activeStep) return false;
    return STEP_TO_SECTION[activeStep] === section;
  };

  const duplicateOutcomeLabels = getDuplicateOutcomeLabels();

  const handleDragStart = (index) => {
    if (isProcessing) return;
    const outcome = marketData.outcomes[index];
    if (outcome.isUnclassified || outcome.locked) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    if (isProcessing) return;
    e.preventDefault();
    const outcome = marketData.outcomes[index];
    if (outcome.isUnclassified) return;
    setDragOverIndex(index);
  };

  const handleDrop = (index) => {
    if (isProcessing) return;
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const target = marketData.outcomes[index];
    if (target.isUnclassified) return;
    const newOutcomes = [...marketData.outcomes];
    const [dragged] = newOutcomes.splice(draggedIndex, 1);
    newOutcomes.splice(index, 0, dragged);
    if (onEdit) onEdit("outcomes", newOutcomes);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleTouchReorder = (fromIndex, direction) => {
    if (isProcessing) return;
    const outcome = marketData.outcomes[fromIndex];
    if (outcome.isUnclassified || outcome.locked) return;
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= marketData.outcomes.length) return;
    const targetOutcome = marketData.outcomes[toIndex];
    if (targetOutcome.isUnclassified) return;
    const newOutcomes = [...marketData.outcomes];
    [newOutcomes[fromIndex], newOutcomes[toIndex]] = [
      newOutcomes[toIndex],
      newOutcomes[fromIndex],
    ];
    if (onEdit) onEdit("outcomes", newOutcomes);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="gm-mdp"
      id="market-preview-top"
      onClick={dismissOnboarding}
    >
      {showOnboarding && (
        <motion.div
          className="gm-mdp-onboarding-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 1.8 }}
        >
          <motion.div
            className="gm-mdp-onboarding-shimmer"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.12), transparent)",
            }}
            initial={{ left: "-10%" }}
            animate={{ left: "110%" }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeInOut" }}
          />
        </motion.div>
      )}

      <ActiveSectionGlow active={isActive("section-question")}>
        <div className="gm-mdp-header">
          <div className="gm-mdp-header-top">
            <div className="gm-mdp-header-badges">
              {marketData.category && (
                <span className="gm-mdp-category">{marketData.category}</span>
              )}
            </div>
          </div>

          <div className="gm-mdp-question-row">
            <div className="gm-mdp-question-col">
              {editingField === "question" ? (
                <div className="gm-mdp-edit-question">
                  <input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="gm-mdp-edit-question-input"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmEdit();
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />
                  <button onClick={confirmEdit} className="gm-mdp-edit-action">
                    <Check className="gm-icon-sm gm-text-primary" />
                  </button>
                  <button onClick={cancelEdit} className="gm-mdp-edit-action">
                    <X className="gm-icon-sm" />
                  </button>
                </div>
              ) : (
                <h1 className="gm-mdp-question-text">
                  {marketData.question}
                  <button
                    onClick={() => {
                      dismissOnboarding();
                      if (onNavigateToStep) onNavigateToStep("question");
                    }}
                    className="gm-mdp-question-btn"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M10 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333V10"
                        stroke="#656565"
                        stroke-width="1.57143"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M15.3065 2.18769C15.638 1.85617 16.0877 1.66992 16.5565 1.66992C17.0254 1.66992 17.475 1.85617 17.8065 2.18769C18.138 2.51921 18.3243 2.96885 18.3243 3.43769C18.3243 3.90653 18.138 4.35617 17.8065 4.68769L10.2957 12.1994C10.0978 12.3971 9.85335 12.5418 9.58485 12.6202L7.19068 13.3202C7.11897 13.3411 7.04296 13.3424 6.97061 13.3238C6.89825 13.3053 6.8322 13.2676 6.77939 13.2148C6.72657 13.162 6.68892 13.096 6.67038 13.0236C6.65185 12.9512 6.6531 12.8752 6.67402 12.8035L7.37402 10.4094C7.45279 10.1411 7.59779 9.8969 7.79568 9.69936L15.3065 2.18769Z"
                        stroke="#656565"
                        stroke-width="1.57143"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </h1>
              )}
            </div>
          </div>

          <ActiveSectionGlow active={isActive("section-liquidity")}>
            <div className="gm-mdp-liq-section">
              <button
                onClick={() => setShowLiquidityEditor(!showLiquidityEditor)}
                className="gm-mdp-liq-toggle"
              >
                <div className="gm-mdp-liq-toggle-text">
                  <p className="gm-mdp-setting-micro-label">Total Volume</p>
                  <p className="gm-mdp-liq-toggle-value">
                    <img
                      src={
                        marketData.currency === "rain"
                          ? "/assets/genrate-market-imgs/rain.svg"
                          : "/assets/poll/usdr.svg"
                      }
                      alt={marketData.currency}
                      className="img-fluid"
                    />
                    {marketData.liquidity?.toLocaleString() || "0"}{" "}
                    {marketData.currency === "rain" ? "RAIN" : "USD"}
                  </p>
                </div>
              </button>
              <AnimatePresence>
                {showLiquidityEditor && (
                  <motion.div
                    initial={{ opacity: 0, y: -5, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -5, scale: 0.97 }}
                    className="gm-mdp-liq-dropdown"
                  >
                    <LiquidityWithFees
                      initialCurrency={marketData.currency || "usdr"}
                      initialAmount={marketData.liquidity || undefined}
                      maxBalance={
                        wallet
                          ? marketData.currency === "rain"
                            ? wallet.balanceRAIN
                            : wallet.balanceUSDT
                          : undefined
                      }
                      gasFeeInUsdt={gasFeeInUsdt}
                      oracleFeeInUsdt={oracleFeeInUsdt}
                      isLoadingFees={isLoadingFees}
                      onAmountChange={onLiquidityAmountChange}
                      onConfirm={(amount, currency) => {
                        if (onEdit) {
                          onEdit("liquidity", amount);
                          if (currency) onEdit("currency", currency);
                        }
                        setShowLiquidityEditor(false);
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ActiveSectionGlow>
        </div>
      </ActiveSectionGlow>

      <div className="gm-mdp-settings">
        <div className="gm-mdp-settings-grid">
          <ActiveSectionGlow active={isActive("section-visibility")}>
            <motion.button
              key={`vis-${marketData.isPublic}`}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              onClick={() => {
                if (onEdit) onEdit("visibility", !marketData.isPublic);
              }}
              className="gm-mdp-setting-card"
            >
              <motion.div
                key={`vis-icon-${marketData.isPublic}`}
                initial={{ rotate: -20, scale: 0.6 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                className={`gm-mdp-setting-icon ${marketData.isPublic ? "gm-mdp-setting-icon--primary" : "gm-mdp-setting-icon--muted"}`}
              >
                {marketData.isPublic ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M11 20.1663C16.0627 20.1663 20.1667 16.0623 20.1667 10.9997C20.1667 5.93706 16.0627 1.83301 11 1.83301C5.93743 1.83301 1.83337 5.93706 1.83337 10.9997C1.83337 16.0623 5.93743 20.1663 11 20.1663Z"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M11 1.83301C8.64626 4.30448 7.33337 7.58669 7.33337 10.9997C7.33337 14.4127 8.64626 17.6949 11 20.1663C13.3538 17.6949 14.6667 14.4127 14.6667 10.9997C14.6667 7.58669 13.3538 4.30448 11 1.83301Z"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1.83337 11H20.1667"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M17.4167 10.083H4.58333C3.57081 10.083 2.75 10.9038 2.75 11.9163V18.333C2.75 19.3455 3.57081 20.1663 4.58333 20.1663H17.4167C18.4292 20.1663 19.25 19.3455 19.25 18.333V11.9163C19.25 10.9038 18.4292 10.083 17.4167 10.083Z"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6.41669 10.083V6.41634C6.41669 5.20077 6.89957 4.03498 7.75911 3.17544C8.61866 2.31589 9.78444 1.83301 11 1.83301C12.2156 1.83301 13.3814 2.31589 14.2409 3.17544C15.1005 4.03498 15.5834 5.20077 15.5834 6.41634V10.083"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                )}
              </motion.div>
              <div className="gm-mdp-setting-text">
                <p className="gm-mdp-setting-micro-label">Visibility</p>
                <motion.p
                  key={`vis-label-${marketData.isPublic}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="gm-mdp-setting-value"
                >
                  {marketData.isPublic ? "Public" : "Private"}
                </motion.p>
              </div>
            </motion.button>
          </ActiveSectionGlow>

          <ActiveSectionGlow active={isActive("section-resolver")}>
            <motion.button
              key={`res-${marketData.resolutionMethod}`}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              onClick={() => {
                if (forceManual) return;
                if (onNavigateToStep) {
                  onNavigateToStep("resolver");
                } else if (onEdit) {
                  onEdit(
                    "resolver",
                    marketData.resolutionMethod === "ai" ? "manual" : "ai",
                  );
                }
              }}
              title={
                forceManual
                  ? "This question can't be resolved by AI. Only manual resolution is available."
                  : undefined
              }
              className={`gm-mdp-setting-card ${
                forceManual
                  ? "gm-mdp-setting-card--disabled"
                  : marketData.resolutionMethod === null
                    ? "gm-mdp-setting-card--unset"
                    : ""
              }`}
            >
              <motion.div
                key={`res-icon-${marketData.resolutionMethod}`}
                initial={{ rotate: 20, scale: 0.6 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                className={`gm-mdp-setting-icon ${marketData.resolutionMethod === "ai" ? "gm-mdp-setting-icon--primary" : marketData.resolutionMethod === null ? "gm-mdp-setting-icon--muted" : "gm-mdp-setting-icon--muted"}`}
              >
                {marketData.resolutionMethod === "ai" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M11 7.33341V3.66675H7.33337"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16.5 7.33325H5.49996C4.48744 7.33325 3.66663 8.15406 3.66663 9.16659V16.4999C3.66663 17.5124 4.48744 18.3333 5.49996 18.3333H16.5C17.5125 18.3333 18.3333 17.5124 18.3333 16.4999V9.16659C18.3333 8.15406 17.5125 7.33325 16.5 7.33325Z"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1.83337 12.8333H3.66671"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M18.3334 12.8333H20.1667"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.75 11.9167V13.7501"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.25 11.9167V13.7501"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M17.4166 19.25V17.4167C17.4166 16.4442 17.0303 15.5116 16.3427 14.8239C15.6551 14.1363 14.7224 13.75 13.75 13.75H8.24998C7.27752 13.75 6.34489 14.1363 5.65725 14.8239C4.96962 15.5116 4.58331 16.4442 4.58331 17.4167V19.25"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M11 10.0833C13.025 10.0833 14.6666 8.44171 14.6666 6.41667C14.6666 4.39162 13.025 2.75 11 2.75C8.97494 2.75 7.33331 4.39162 7.33331 6.41667C7.33331 8.44171 8.97494 10.0833 11 10.0833Z"
                      stroke="#E6FF00"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                )}
              </motion.div>
              <div className="gm-mdp-setting-text">
                <p className="gm-mdp-setting-micro-label">Resolver</p>
                <motion.p
                  key={`res-label-${marketData.resolutionMethod}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="gm-mdp-setting-value"
                >
                  {marketData.resolutionMethod === "ai"
                    ? "AI Auto"
                    : marketData.resolutionMethod === "manual"
                      ? "Manual"
                      : "Not set"}
                </motion.p>
              </div>
            </motion.button>
          </ActiveSectionGlow>

          <div className="gm-mdp-enddate-section">
            <button
              onClick={() => startDatePickerRef.current?.open()}
              className="gm-mdp-setting-card"
            >
              <div className="gm-mdp-setting-icon gm-mdp-setting-icon--primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                >
                  <path
                    d="M7.33337 1.83325V5.49992"
                    stroke="#E6FF00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14.6666 1.83325V5.49992"
                    stroke="#E6FF00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M17.4167 3.66675H4.58333C3.57081 3.66675 2.75 4.48756 2.75 5.50008V18.3334C2.75 19.3459 3.57081 20.1667 4.58333 20.1667H17.4167C18.4292 20.1667 19.25 19.3459 19.25 18.3334V5.50008C19.25 4.48756 18.4292 3.66675 17.4167 3.66675Z"
                    stroke="#E6FF00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M2.75 9.16675H19.25"
                    stroke="#E6FF00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div className="gm-mdp-setting-text">
                <p className="gm-mdp-setting-micro-label">Launches</p>
                <p className="gm-mdp-setting-value">
                  {marketData.startDate
                    ? format(new Date(marketData.startDate), "MMM dd, hh:mm a")
                    : format(new Date(), "MMM dd, hh:mm a")}
                </p>
              </div>
            </button>
            <div className="gm-mdp-hidden-picker">
              <DateTimePicker
                ref={startDatePickerRef}
                value={marketData.startDate}
                onChange={(date) => {
                  if (date && onEdit) {
                    const d = new Date(date);
                    if (d >= new Date(new Date().toDateString())) {
                      onEdit("startDate", d);
                    }
                  }
                }}
                minDateTime={new Date()}
                placeholder="dd/MM/yyyy hh:mm"
                format="dd/MM/yyyy hh:mm aa"
                oneTap={false}
                menuClassName="gm-picker-popup-dark"
              />
            </div>
          </div>

          <ActiveSectionGlow active={isActive("section-resolution")}>
            <div className="gm-mdp-enddate-section">
              <button
                onClick={() => endDatePickerRef.current?.open()}
                className="gm-mdp-setting-card"
              >
                <div
                  className={`gm-mdp-setting-icon ${daysRemaining <= 7 ? "gm-mdp-setting-icon--danger" : "gm-mdp-setting-icon--primary"}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M14.6666 12.8333V14.8499L16.1333 15.7666"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14.6666 1.83325V5.49992"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M19.25 6.87508V5.50008C19.25 5.01385 19.0568 4.54754 18.713 4.20372C18.3692 3.8599 17.9029 3.66675 17.4167 3.66675H4.58333C4.0971 3.66675 3.63079 3.8599 3.28697 4.20372C2.94315 4.54754 2.75 5.01385 2.75 5.50008V18.3334C2.75 18.8196 2.94315 19.286 3.28697 19.6298C3.63079 19.9736 4.0971 20.1667 4.58333 20.1667H7.79167"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2.75 9.16675H7.33333"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7.33337 1.83325V5.49992"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14.6666 20.1667C17.7042 20.1667 20.1666 17.7043 20.1666 14.6667C20.1666 11.6292 17.7042 9.16675 14.6666 9.16675C11.6291 9.16675 9.16663 11.6292 9.16663 14.6667C9.16663 17.7043 11.6291 20.1667 14.6666 20.1667Z"
                      stroke="#FF5C32"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="gm-mdp-setting-text">
                  <p className="gm-mdp-setting-micro-label">Ends</p>
                  <p className="gm-mdp-setting-value">
                    {resolutionDateObj
                      ? format(resolutionDateObj, "MMM dd, hh:mm a")
                      : "Not set"}
                  </p>
                </div>
              </button>
              <div className="gm-mdp-hidden-picker">
                <DateTimePicker
                  ref={endDatePickerRef}
                  value={resolutionDateObj}
                  onChange={(date) => {
                    if (date && onEdit) {
                      const d = new Date(date);
                      if (d >= new Date(new Date().toDateString())) {
                        onEdit("resolution", d);
                      }
                    }
                  }}
                  minDateTime={
                    marketData.startDate
                      ? new Date(marketData.startDate)
                      : new Date()
                  }
                  placeholder="dd/MM/yyyy hh:mm"
                  format="dd/MM/yyyy hh:mm aa"
                  oneTap={false}
                  menuClassName="gm-picker-popup-dark leftalign"
                />
              </div>
            </div>
          </ActiveSectionGlow>
        </div>
      </div>

      <ActiveSectionGlow active={isActive("section-description")}>
        <div className="gm-mdp-info-wrap">
          <div className="gm-mdp-info-card">
          
              <h3 className="gm-mdp-info-title gm-mdp-info-title--static">
                Market Info
              </h3>

            <AnimatePresence>
              {(marketInfoOpen || !isMobile) && (
                <motion.div
                  initial={isMobile ? { height: 0, opacity: 0 } : false}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={isMobile ? { height: 0, opacity: 0 } : undefined}
                  transition={{ duration: 0.2 }}
                  className="gm-mdp-info-content"
                >
                  <div className="gm-mdp-info-creator">
                    <div className="gm-mdp-info-creator-avatar">
                      {userProfile?.profilePic ? (
                        <img
                          src={userProfile.profilePic}
                          alt="creator"
                          className="gm-mdp-info-creator-img"
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="20"
                          viewBox="0 0 22 20"
                          fill="none"
                        >
                          <path
                            d="M18 21V19C18 17.9391 17.5786 16.9217 16.8284 16.1716C16.0783 15.4214 15.0609 15 14 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                            stroke="#DFDFDF"
                            stroke-opacity="0.5"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M11 11C13.2091 11 15 9.20914 15 7C15 4.79086 13.2091 3 11 3C8.79086 3 7 4.79086 7 7C7 9.20914 8.79086 11 11 11Z"
                            stroke="#DFDFDF"
                            stroke-opacity="0.5"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <p className="gm-mdp-info-creator-text">
                      Created by{" "}
                      <span className="gm-mdp-info-creator-addr">
                        {userProfile?.name
                          ? userProfile.name
                          : wallet?.address
                            ? `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`
                            : "0x..."}
                      </span>{" "}
                      • {format(new Date(), "MMM dd, yyyy")}{" "}
                      {format(new Date(), "HH:mm")}
                    </p>
                  </div>

                  <div className="gm-mdp-info-rules">
                    <div className="gm-mdp-info-rules-header">
                      <p className="gm-mdp-info-rules-label">Market Rules</p>
               
                    </div>
                    {editingField === "description" ? (
                      <div className="gm-mdp-info-desc-edit">
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="gm-mdp-info-desc-textarea"
                          rows={5}
                          autoFocus
                        />
                        <div className="gm-mdp-info-desc-edit-actions">
                          <button
                            onClick={confirmEdit}
                            className="gm-mdp-info-desc-action"
                          >
                            <Check className="gm-icon-xxs gm-text-primary" />{" "}
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="gm-mdp-info-desc-action"
                          >
                            <X className="gm-icon-xxs" /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() =>
                          startEditing("description", marketData.description)
                        }
                        className="gm-mdp-info-desc-btn"
                      >
                        <p
                          className={`gm-mdp-info-desc-text ${isActive("section-description") ? "gm-mdp-info-desc-text--active" : ""}`}
                        >
                          {marketData.description}
                        </p>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 17 17"
                          fill="none"
                          style={{ flexShrink: "0" }}
                        >
                          <path
                            d="M8.5 2.125H3.54167C3.16594 2.125 2.80561 2.27426 2.53993 2.53993C2.27426 2.80561 2.125 3.16594 2.125 3.54167V13.4583C2.125 13.8341 2.27426 14.1944 2.53993 14.4601C2.80561 14.7257 3.16594 14.875 3.54167 14.875H13.4583C13.8341 14.875 14.1944 14.7257 14.4601 14.4601C14.7257 14.1944 14.875 13.8341 14.875 13.4583V8.5"
                            stroke="#656565"
                            stroke-width="1.33571"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M13.0106 1.86002C13.2924 1.57823 13.6746 1.41992 14.0731 1.41992C14.4716 1.41992 14.8538 1.57823 15.1356 1.86002C15.4174 2.14182 15.5757 2.52401 15.5757 2.92252C15.5757 3.32104 15.4174 3.70323 15.1356 3.98502L8.7514 10.3699C8.5832 10.538 8.37542 10.661 8.14719 10.7276L6.11215 11.3226C6.0512 11.3404 5.98659 11.3415 5.92508 11.3257C5.86358 11.31 5.80744 11.278 5.76255 11.2331C5.71765 11.1882 5.68565 11.1321 5.66989 11.0705C5.65414 11.009 5.6552 10.9444 5.67298 10.8835L6.26798 8.84844C6.33494 8.62039 6.45819 8.41286 6.6264 8.24494L13.0106 1.86002Z"
                            stroke="#656565"
                            stroke-width="1.33571"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </ActiveSectionGlow>

      <ActiveSectionGlow active={isActive("section-outcomes")}>
        <div className="gm-mdp-outcomes-section">
    
          {showOnboarding && (
            <motion.div
              className="gm-mdp-outcomes-highlight"
              animate={{ opacity: [0, 0.6, 0], scale: [0.98, 1.02, 0.98] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
          {showOnboarding && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="gm-mdp-outcomes-tap-hint"
            >
              <Pencil className="gm-icon-xxs" />
              Tap to edit
            </motion.div>
          )}
          {marketData.outcomes.map((outcome, index) => {
            const isEditingOutcome =
              editingField === `outcome-${index}` && !outcome.isUnclassified;
            const isDuplicateOutcome =
              !outcome.isUnclassified &&
              !!outcome.label.trim() &&
              duplicateOutcomeLabels.has(normalizeOutcomeLabel(outcome.label));
            const isDuplicateDraft = isEditingOutcome
              ? isDuplicateOutcomeLabel(editValue, index)
              : false;
            const isInvalidDraft = isEditingOutcome
              ? !editValue.trim() || isDuplicateDraft
              : false;

            return (
              <motion.div
                key={outcome.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{
                  opacity: draggedIndex === index ? 0.5 : 1,
                  y: 0,
                  scale:
                    dragOverIndex === index && draggedIndex !== index
                      ? 1.02
                      : 1,
                }}
                transition={{ delay: index * 0.1 }}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={
                  dragOverIndex === index &&
                  draggedIndex !== null &&
                  draggedIndex !== index &&
                  !outcome.isUnclassified
                    ? "gm-mdp-outcome-dragover"
                    : ""
                }
              >
                {isEditingOutcome ? (
                  <div
                    className="gm-mdp-outcome-editing"
                    style={{
                      backgroundColor: getOutcomeColor(outcome.id, index),
                    }}
                  >
                    <div className="gm-mdp-outcome-editing-body">
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className={`gm-mdp-outcome-editing-input ${isInvalidDraft ? "gm-mdp-outcome-editing-input--invalid" : ""}`}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveOutcomeLabel(index, editValue);
                          }
                          if (e.key === "Escape") cancelEdit();
                        }}
                      />
                      {isDuplicateDraft && (
                        <motion.p
                          className="gm-mdp-outcome-dup-msg"
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          Duplicate outcome name is not allowed.
                        </motion.p>
                      )}
                    </div>
                    <button
                      disabled={isInvalidDraft || isProcessing}
                      onClick={() => saveOutcomeLabel(index, editValue)}
                      className="gm-mdp-outcome-editing-action"
                    >
                      <Check className="gm-icon-xxs" />
                    </button>
                    <button
                      disabled={isProcessing}
                      onClick={() => {
                        if (isProcessing) return;
                        const nonUnclassified = marketData.outcomes.filter(
                          (o) => !o.isUnclassified,
                        );
                        if (
                          nonUnclassified.length > 2 &&
                          !outcome.isUnclassified
                        ) {
                          const remaining = marketData.outcomes.filter(
                            (_, i) => i !== index,
                          );
                          const unlocked = remaining.filter(
                            (o) => !o.locked && !o.isUnclassified,
                          );
                          if (unlocked.length > 0) {
                            const lockedTotal = remaining
                              .filter((o) => o.locked || o.isUnclassified)
                              .reduce((s, o) => s + o.probability, 0);
                            const available = 100 - lockedTotal;
                            const eqShare = Math.floor(
                              available / unlocked.length,
                            );
                            const lastSh =
                              available - eqShare * (unlocked.length - 1);
                            const uIds = unlocked.map((o) => o.id);
                            const redistributed = remaining.map((o) => {
                              const ui = uIds.indexOf(o.id);
                              if (ui < 0) return o;
                              return {
                                ...o,
                                probability:
                                  ui === uIds.length - 1
                                    ? Math.max(1, lastSh)
                                    : Math.max(1, eqShare),
                              };
                            });
                            if (onEdit) onEdit("outcomes", redistributed);
                          } else {
                            if (onEdit) onEdit("outcomes", remaining);
                          }
                          setEditingField(null);
                        } else {
                          cancelEdit();
                        }
                      }}
                      className="gm-mdp-outcome-editing-action"
                    >
                      <X className="gm-icon-xxs" />
                    </button>
                  </div>
                ) : (
                  <div className="gm-mdp-outcome-row">
                    {!outcome.isUnclassified && !outcome.locked && !isProcessing ? (
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          handleDragStart(index);
                        }}
                        className="gm-mdp-grip"
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          const startY = e.touches[0].clientY;
                          const threshold = 30;
                          const handleTouchMove = (ev) => {
                            const diff = ev.touches[0].clientY - startY;
                            if (Math.abs(diff) > threshold) {
                              handleTouchReorder(
                                index,
                                diff < 0 ? "up" : "down",
                              );
                              cleanup();
                            }
                          };
                          const cleanup = () => {
                            window.removeEventListener(
                              "touchmove",
                              handleTouchMove,
                            );
                            window.removeEventListener("touchend", cleanup);
                          };
                          window.addEventListener(
                            "touchmove",
                            handleTouchMove,
                            { passive: true },
                          );
                          window.addEventListener("touchend", cleanup);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12C8 12.5523 8.44772 13 9 13Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M9 6C9.55228 6 10 5.55228 10 5C10 4.44772 9.55228 4 9 4C8.44772 4 8 4.44772 8 5C8 5.55228 8.44772 6 9 6Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M9 20C9.55228 20 10 19.5523 10 19C10 18.4477 9.55228 18 9 18C8.44772 18 8 18.4477 8 19C8 19.5523 8.44772 20 9 20Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M15 13C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11C14.4477 11 14 11.4477 14 12C14 12.5523 14.4477 13 15 13Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M15 6C15.5523 6 16 5.55228 16 5C16 4.44772 15.5523 4 15 4C14.4477 4 14 4.44772 14 5C14 5.55228 14.4477 6 15 6Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M15 20C15.5523 20 16 19.5523 16 19C16 18.4477 15.5523 18 15 18C14.4477 18 14 18.4477 14 19C14 19.5523 14.4477 20 15 20Z"
                            stroke="#E8E8E8"
                            stroke-opacity="0.3"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div className="gm-mdp-grip-spacer">
                        <div className="gm-mdp-grip-spacer-inner" />
                      </div>
                    )}
                    <div className="gm-mdp-outcome-bar-wrap">
                      <SwipableOutcomeBar
                        outcome={outcome}
                        index={index}
                        color={
                          outcome.isUnclassified
                            ? "#ffffff"
                            : getOutcomeColor(outcome.id, index)
                        }
                        disabled={isProcessing}
                        onProbabilityChange={handleOutcomeProbabilityChange}
                        onEdit={() => {
                          if (isProcessing) return;
                          startEditing(`outcome-${index}`, outcome.label);
                        }}
                        onRemove={() => {
                          if (isProcessing) return;
                          if (outcome.isUnclassified) return;
                          const remaining = marketData.outcomes.filter(
                            (_, i) => i !== index,
                          );
                          const unlocked = remaining.filter(
                            (o) => !o.locked && !o.isUnclassified,
                          );
                          if (unlocked.length > 0) {
                            const lockedTotal = remaining
                              .filter((o) => o.locked || o.isUnclassified)
                              .reduce((s, o) => s + o.probability, 0);
                            const available = 100 - lockedTotal;
                            const equalShare = Math.floor(
                              available / unlocked.length,
                            );
                            const lastShare =
                              available - equalShare * (unlocked.length - 1);
                            const unlockedIds = unlocked.map((o) => o.id);
                            const redistributed = remaining.map((o) => {
                              const idx = unlockedIds.indexOf(o.id);
                              if (idx < 0) return o;
                              return {
                                ...o,
                                probability:
                                  idx === unlockedIds.length - 1
                                    ? Math.max(1, lastShare)
                                    : Math.max(1, equalShare),
                              };
                            });
                            if (onEdit) onEdit("outcomes", redistributed);
                          } else {
                            if (onEdit) onEdit("outcomes", remaining);
                          }
                        }}
                        onToggleLock={() => {
                          if (isProcessing) return;
                          if (outcome.isUnclassified) return;
                          const newOutcomes = [...marketData.outcomes];
                          newOutcomes[index] = {
                            ...newOutcomes[index],
                            locked: !newOutcomes[index].locked,
                          };
                          if (onEdit) onEdit("outcomes", newOutcomes);
                        }}
                        canRemove={
                          marketData.outcomes.filter((o) => !o.isUnclassified)
                            .length > 2
                        }
                        isDuplicate={isDuplicateOutcome}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
          {marketData.outcomes.length < 12 && (
            <div className="gm-mdp-outcome-row">
              <div className="gm-mdp-grip-spacer">
                <div className="gm-mdp-grip-spacer-inner" />
              </div>
              <button
                onClick={addOutcome}
                disabled={isProcessing}
                className="gm-mdp-add-outcome-btn"
              >
                Add Outcome
              </button>
            </div>
          )}
        </div>
      </ActiveSectionGlow>

      <AnimatePresence>
        {walletError && walletError !== "not-connected" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="gm-mdp-wallet-error-wrap"
          >
            <div className="gm-mdp-wallet-error">
              <AlertTriangle className="gm-icon-sm gm-text-destructive gm-mdp-wallet-error-icon" />
              <p className="gm-mdp-wallet-error-text">
                {walletError === "insufficient-balance"
                  ? "Insufficient balance to provide the selected liquidity."
                  : walletError === "tx-rejected"
                    ? "Transaction was cancelled. The market has not been deployed."
                    : walletError === "deploy-failed"
                      ? "Deployment failed due to a network error. You may retry."
                      : null}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="gm-mdp-launch-section">
        {wallet && !wallet.connected ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="gm-mdp-connect-wallet"
          >
            <button
              onClick={onConnectWallet}
              className="gm-mdp-connect-wallet-btn"
              style={{
                background:
                  "linear-gradient(to right, hsl(var(--primary)), hsl(var(--primary) / 0.8))",
              }}
            >
              <Wallet className="gm-icon-sm" />
              Connect Wallet to Launch
            </button>
            <p className="gm-mdp-connect-wallet-hint">
              Connect your wallet to deploy this market on-chain
            </p>
          </motion.div>
        ) : (
          <div className="gm-mdp-outcome-row">
            <div className="gm-mdp-grip-spacer">
              <div className="gm-mdp-grip-spacer-inner" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                width: "100%",
              }}
            >
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className="gm-mdp-launch-btn"
                style={{ boxShadow: "0 4px 14px hsl(var(--primary) / 0.3)" }}
              >
                {isProcessing ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="gm-spinner"
                  />
                ) : (
                  <>Launch Market</>
                )}
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
